import { IMAGE_BASE_URL } from '../../config/api';
import beetrootImg from '../../assets/images/beetroot-detox.jpg';
import chapatiImg from '../../assets/images/chapati-product.jpg';
import methiImg from '../../assets/images/methi-paratha.jpg';
import southThaliMenuImg from '../../assets/images/south-thali-menu.png';
import biryaniImg from '../../assets/images/biryani-raita.jpg';
import masalaDosaImg from '../../assets/images/masala-dosa.jpg';

/** Used when remote item image and fallback both fail (same asset as legacy menu hero). */
export const DEFAULT_MENU_ITEM_IMAGE = southThaliMenuImg;

/** Same pattern as digirasa-client: `{baseUrl}/utils/downloadImage/item/{itemID}` */
export function buildItemImageUrl(itemId) {
  const id = Number(itemId);
  if (!Number.isFinite(id)) return '';
  return `${IMAGE_BASE_URL}/utils/downloadImage/item/${id}`;
}

/** Same pattern as digirasa-client menu list: `{baseUrl}/utils/downloadImage/menu/{menu.id}` */
export function buildMenuImageUrl(menuId) {
  const id = Number(menuId);
  if (!Number.isFinite(id)) return '';
  return `${IMAGE_BASE_URL}/utils/downloadImage/menu/${id}`;
}

const trimText = (value, fallback = '') =>
  typeof value === 'string' ? value.replace(/\s+/g, ' ').trim() : fallback;

const toBool = (value, fallback = false) => {
  if (typeof value === 'boolean') return value;
  if (value == null) return fallback;
  return Boolean(value);
};

/**
 * Whether the item can be added to cart from Our Menu.
 * Variable products are orderable if at least one option is enabled.
 * @param {{ kind?: string, variants?: array, insufficientInventory?: boolean, notAvailable?: boolean }} item
 */
export function isMenuItemOrderable(item) {
  if (isVariableMenuItem(item)) {
    return getEnabledVariants(item).length > 0;
  }
  return !toBool(item?.insufficientInventory, false) && !toBool(item?.notAvailable, false);
}

/**
 * Label for disabled menu items. Prefer schedule unavailability over stock.
 * @param {{ kind?: string, variants?: array, insufficientInventory?: boolean, notAvailable?: boolean }} item
 */
export function getMenuItemUnavailableLabel(item) {
  if (isVariableMenuItem(item) && getEnabledVariants(item).length === 0) {
    return 'Currently unavailable';
  }
  if (toBool(item?.notAvailable, false)) return 'Not available';
  if (toBool(item?.insufficientInventory, false)) return 'Out of stock';
  return '';
}

export function isVariableMenuItem(item) {
  return item?.kind === 'variable' && Array.isArray(item?.variants) && item.variants.length > 0;
}

export function isVariantOrderable(variant) {
  if (!variant) return false;
  if (variant.disabled === true) return false;
  if (variant.active === false) return false;
  if (variant.status === false) return false;
  if (toBool(variant.insufficientInventory, false)) return false;
  if (toBool(variant.notAvailable, false)) return false;
  if (variant.price == null || !Number.isFinite(Number(variant.price))) return false;
  return true;
}

export function getEnabledVariants(item) {
  if (!Array.isArray(item?.variants)) return [];
  return item.variants.filter(isVariantOrderable);
}

/** Compact card hint for variable products, e.g. "2 options" or "200 g · 500 g". */
export function getVariantOptionsHint(item) {
  if (!isVariableMenuItem(item)) return '';
  const variants = Array.isArray(item.variants) ? item.variants : [];
  const n = variants.length;
  if (n === 0) return 'Options available';
  if (n <= 2) {
    const labels = variants
      .map((v) => trimText(v.label || v.sizeLabel, ''))
      .filter(Boolean);
    if (labels.length === n) return labels.join(' · ');
  }
  return `${n} option${n === 1 ? '' : 's'}`;
}

/** @deprecated Use getVariantOptionsHint */
export const getVariantSizesHint = getVariantOptionsHint;

export function getVariantUnavailableLabel(variant) {
  if (!variant) return 'Unavailable';
  if (toBool(variant.notAvailable, false)) return 'Not available';
  if (toBool(variant.insufficientInventory, false)) return 'Out of stock';
  if (variant.active === false || variant.status === false || variant.disabled === true) {
    return 'Unavailable';
  }
  if (variant.price == null || !Number.isFinite(Number(variant.price))) return 'No price';
  return 'Unavailable';
}

/**
 * Cart / plan line for a chosen child SKU under a parent display product.
 */
export function buildCartLineFromVariant(parentItem, variant) {
  const price = Number(variant?.price ?? 0);
  const label = trimText(variant?.label || variant?.sizeLabel, '');
  const parentName = trimText(parentItem?.name, 'Menu Item');
  const childId = Number(variant?.itemID ?? variant?.id);
  const variantImage =
    trimText(variant?.image, '') ||
    (Number.isFinite(childId) ? buildItemImageUrl(childId) : '');
  return {
    id: childId,
    name: parentName,
    price: Number.isFinite(price) ? price : 0,
    priceDisplay: `₹${(Number.isFinite(price) ? price : 0).toFixed(2)}`,
    image: variantImage || parentItem?.image,
    category: parentItem?.category,
    variantLabel: label || undefined,
    parentItemId: parentItem?.parentId ?? parentItem?.id,
  };
}

const parseVariation = (raw) => {
  const itemID = Number(raw?.itemID ?? raw?.item_id ?? raw?.id);
  if (!Number.isFinite(itemID)) return null;
  const priceRaw = raw?.price;
  const price =
    priceRaw == null || priceRaw === ''
      ? null
      : Number(priceRaw);
  const label = trimText(raw?.sizeLabel ?? raw?.label ?? raw?.name, '');
  const active = raw?.active !== false;
  const status = raw?.status !== false;
  const insufficientInventory = toBool(raw?.insufficientInventory, false);
  const notAvailable = toBool(raw?.notAvailable, false);
  const disabled =
    !active ||
    !status ||
    insufficientInventory ||
    notAvailable ||
    price == null ||
    !Number.isFinite(price);
  const name = trimText(raw?.name, '');
  const description = trimText(raw?.description, '');

  return {
    id: Number(raw?.id) || itemID,
    itemID,
    label: label || `Option ${itemID}`,
    sizeLabel: label || `Option ${itemID}`,
    sortOrder: Number(raw?.sortOrder ?? 0) || 0,
    active,
    status,
    price: Number.isFinite(price) ? price : null,
    priceDisplay:
      price != null && Number.isFinite(price) ? `₹${price.toFixed(2)}` : null,
    insufficientInventory,
    notAvailable,
    disabled,
    name,
    description,
    image: buildItemImageUrl(itemID),
  };
};

/**
 * Flattens DigiRasa public menu payload from GET /menu/activeMenuWithItems
 * (see https://api.digirasa.com/docs — /menu/getMenu requires auth).
 * Parents with `variations[]` become `kind: 'variable'` display products.
 * Child SKUs are already omitted by the API when linked; we still skip flagged children.
 */
export const flattenActiveMenuWithItems = (payload) => {
  const menus = Array.isArray(payload?.data) ? payload.data : Array.isArray(payload) ? payload : [];
  const byId = new Map();

  for (const top of menus) {
    if (!top || top.status === false) continue;
    const menuName = trimText(top.name, 'Menu');
    const topMenuId = Number(top.id);

    for (const cat of top.category || []) {
      if (!cat || cat.status === false) continue;
      const category = trimText(cat.name, 'General');

      for (const mi of cat.menuItems || []) {
        if (!mi || mi.status === false) continue;
        // Safety: skip child rows if API still flags them (admin payloads).
        if (mi.isVariationChild === true) continue;

        const id = Number(mi.itemID ?? mi.item_id);
        if (!Number.isFinite(id) || byId.has(id)) continue;

        const insufficientInventory = toBool(mi.insufficientInventory, false);
        const notAvailable = toBool(mi.notAvailable, false);

        const rawVariants = Array.isArray(mi.variations) ? mi.variations : [];
        const variants = rawVariants
          .map(parseVariation)
          .filter(Boolean)
          .sort((a, b) => a.sortOrder - b.sortOrder || a.itemID - b.itemID);

        const enabledPrices = variants
          .filter(isVariantOrderable)
          .map((v) => v.price)
          .filter((p) => p != null && Number.isFinite(p));
        const fromPrice =
          mi.fromPrice != null && Number.isFinite(Number(mi.fromPrice))
            ? Number(mi.fromPrice)
            : enabledPrices.length
              ? Math.min(...enabledPrices)
              : null;

        const base = {
          id,
          item_id: id,
          name: trimText(mi.name, 'Menu Item'),
          price: Number(mi.price ?? 0),
          category,
          menu_name: menuName,
          topMenuId: Number.isFinite(topMenuId) ? topMenuId : undefined,
          description: trimText(mi.description, ''),
          available: mi.status !== false,
          insufficientInventory,
          notAvailable,
        };

        if (variants.length > 0 || mi.isVariationParent === true) {
          byId.set(id, {
            ...base,
            kind: 'variable',
            parentId: id,
            variants,
            fromPrice,
          });
        } else {
          byId.set(id, { ...base, kind: 'simple' });
        }
      }
    }
  }

  return [...byId.values()];
};

const resolveFallbackImage = (item) => {
  const text = `${item?.name || ''} ${item?.category || ''} ${item?.menuName || ''}`.toLowerCase();

  if (
    text.includes('beetroot') ||
    text.includes('ragi') ||
    text.includes('lassi') ||
    text.includes('java') ||
    text.includes('drink')
  )
    return beetrootImg;
  if (text.includes('methi')) return methiImg;
  if (
    text.includes('chapathi') ||
    text.includes('roti') ||
    text.includes('paratha') ||
    text.includes('naan')
  )
    return chapatiImg;
  if (
    text.includes('dosa') ||
    text.includes('upma') ||
    text.includes('pesarattu') ||
    text.includes('idly')
  )
    return masalaDosaImg;
  if (
    text.includes('biryani') ||
    text.includes('fried rice') ||
    text.includes('pulao') ||
    text.includes('rice')
  )
    return biryaniImg;
  return southThaliMenuImg;
};

const resolveMenuImage = (item) => {
  const id = Number(item?.id ?? item?.item_id ?? item?.itemID);
  const url = Number.isFinite(id) ? buildItemImageUrl(id) : '';
  return url || resolveFallbackImage(item);
};

export const formatMenuItem = (item) => {
  const price = Number(item.price ?? item.menu_item_price ?? 0);
  const category = item.category || item.category_name || 'General';
  const name = item.name || item.item_name || 'Menu Item';
  const menuName = item.menuName || item.menu_name || category || 'Menu';
  const topMenuIdNum = Number(item.topMenuId);
  const sectionMenuImage = Number.isFinite(topMenuIdNum) ? buildMenuImageUrl(topMenuIdNum) : '';
  const kind = item.kind === 'variable' ? 'variable' : 'simple';
  const variants = (Array.isArray(item.variants) ? item.variants : []).map((v) => {
    const childId = Number(v?.itemID ?? v?.id);
    const vName = trimText(v?.name || v?.label || v?.sizeLabel, '');
    return {
      ...v,
      image: v?.image || (Number.isFinite(childId) ? buildItemImageUrl(childId) : ''),
      fallbackImage:
        v?.fallbackImage ||
        resolveFallbackImage({ name: vName, category, menuName }),
      description: trimText(v?.description, ''),
    };
  });
  const fromPrice =
    item.fromPrice != null && Number.isFinite(Number(item.fromPrice))
      ? Number(item.fromPrice)
      : null;
  const unitPrice = Number.isFinite(price) ? price : 0;
  const priceDisplay =
    kind === 'variable' && fromPrice != null
      ? `From ₹${fromPrice.toFixed(2)}`
      : item.priceDisplay || `₹${unitPrice.toFixed(2)}`;

  return {
    ...item,
    id: Number(item.id ?? item.item_id ?? item.itemID ?? Date.now()),
    name,
    menuName,
    topMenuId: Number.isFinite(topMenuIdNum) ? topMenuIdNum : undefined,
    kind,
    parentId: kind === 'variable' ? Number(item.parentId ?? item.id) : undefined,
    variants,
    fromPrice,
    fromPriceDisplay:
      fromPrice != null ? `From ₹${fromPrice.toFixed(2)}` : null,
    price: unitPrice,
    priceDisplay,
    image: resolveMenuImage({
      ...item,
      id: Number(item.id ?? item.item_id ?? item.itemID),
      name,
      category,
      menuName,
    }),
    fallbackImage: resolveFallbackImage({ name, category, menuName }),
    sectionMenuImage,
    category,
    description:
      item.description ||
      item.item_description ||
      item.menu_description ||
      `${category} · Fresh daily · No preservatives`,
    available: item.available !== false,
    insufficientInventory: toBool(item.insufficientInventory, false),
    notAvailable: toBool(item.notAvailable, false),
  };
};
